import ReceiptIcon from '@mui/icons-material/Receipt.js';
import { Stack } from '@mui/material';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import {
  ActionButton,
  BrowserOnly,
  CopyQRCode,
  QrCodeShowButton,
} from '@woovi/ui';

import type { PaymentMethodPixSelected_charge$key } from './__generated__/PaymentMethodPixSelected_charge.graphql.ts';
import PaymentMethodPixQrCode from './PaymentMethodPixQrCode.tsx';
import PaymentMethodPixValue from './PaymentMethodPixValue.tsx';
import { useChargePaymentMethod } from '../../charge/useChargePaymentMethod.tsx';
import PaymentLinkActiveCashbackFidelityBalance from '../active/paymentLinkActive/PaymentLinkActiveCashbackFidelityBalance.tsx';
import PaymentLinkActiveEarnCashback from '../active/paymentLinkActive/PaymentLinkActiveEarnCashback.tsx';
import PaymentLinkActiveEarnCashbackFidelity from '../active/paymentLinkActive/PaymentLinkActiveEarnCashbackFidelity.tsx';
import PaymentLinkActiveEarnDiscount from '../active/paymentLinkActive/PaymentLinkActiveEarnDiscount.tsx';
import PaymentLinkActiveInterestsSettings from '../active/paymentLinkActive/PaymentLinkActiveInterestsSettings.tsx';
import PaymentLinkActiveOpenBankInfo from '../active/paymentLinkActive/PaymentLinkActiveOpenBankInfo.tsx';
import ChargePrintPDFButton from '../active/paymentLinkActive/pdf/ChargePrintPDF.tsx';
import PaymentLinkAdditionalInfo from '../paymentLink/PaymentLinkAdditionalInfo.tsx';
import PaymentLinkComment from '../paymentLink/PaymentLinkComment.tsx';
import PaymentLinkShareButton from '../paymentLink/PaymentLinkShareButton.tsx';
import PaymentMethodPaymentDeadline from '../paymentMethod/PaymentMethodPaymentDeadline.tsx';
import PaymentMethodSelectedCustomerRegister from '../paymentMethod/PaymentMethodSelectedCustomerRegister.tsx';
import PaymentMethodTransactionDetails from '../paymentMethod/PaymentMethodTransactionDetails.tsx';

type PaymentLinkActiveProps = {
  charge: PaymentMethodPixSelected_charge$key;
  alwaysShowQrCode?: boolean;
  allowSimulatePayment?: boolean;
};

const PaymentMethodPixSelected = (props: PaymentLinkActiveProps): ReactNode => {
  const [qrCodeShow, setQrCodeShow] = useState(false);
  const [copyQRCodeError, setCopyQRCodeError] = useState(false);
  const { t } = useTranslation();

  const charge = useFragment<PaymentMethodPixSelected_charge$key>(
    graphql`
      fragment PaymentMethodPixSelected_charge on Charge {
        id
        brCode
        identifier
        customerRegister
        customer {
          id
        }
        company {
          nameFriendly
          logo {
            url(fitIn: true)
          }
          projectType
          taxID {
            taxID
          }
        }
        paymentMethods {
          ... on PaymentMethodPix {
            brCode
          }
        }
        ...PaymentMethodPixValue_charge
        ...PaymentLinkComment_charge
        ...PaymentLinkActiveEarnDiscount_charge
        ...PaymentLinkActiveEarnCashback_charge
        ...PaymentLinkActiveCashbackFidelityBalance_charge
        ...PaymentLinkActiveEarnCashbackFidelity_charge
        ...PaymentLinkShareButton_charge
        ...PaymentLinkActiveInterestsSettings_charge
        ...PaymentLinkAdditionalInfo_charge
        ...useChargePaymentMethod_charge
        ...PaymentMethodPixQrCode_charge
        ...PaymentMethodTransactionDetails_charge
        ...PaymentMethodPaymentDeadline_charge
        ...PaymentMethodSelectedCustomerRegister_charge
        ...ChargePrintPDF_charge
      }
    `,
    props.charge,
  );

  const { pix } = useChargePaymentMethod(charge);

  if (!charge?.customer && charge.customerRegister) {
    return <PaymentMethodSelectedCustomerRegister charge={charge} />;
  }

  const renderShowQrCode = (): ReactNode => {
    if (!props.alwaysShowQrCode) {
      return (
        <QrCodeShowButton
          qrCodeShow={qrCodeShow}
          setQrCodeShow={setQrCodeShow}
        />
      );
    }

    return null;
  };

  const handleCopyQRCodeError = (error: boolean) => {
    setCopyQRCodeError(error);
  };

  return (
    <Stack sx={{ gap: 2 }}>
      <Stack sx={{ alignItems: 'center' }}>
        <PaymentMethodPixValue charge={charge} />
        <PaymentLinkComment charge={charge} />
      </Stack>
      <Stack sx={{ width: '100%', alignItems: 'center', gap: 1 }}>
        <Stack>
          <PaymentLinkActiveEarnDiscount charge={charge} />
          <PaymentLinkActiveEarnCashback charge={charge} />
          <PaymentLinkActiveCashbackFidelityBalance charge={charge} />
          <PaymentLinkActiveEarnCashbackFidelity charge={charge} />
        </Stack>
        <Stack
          sx={{
            width: '100%',
            maxWidth: (theme) => theme.sizing.maxPixWidth,
            gap: 1,
          }}
        >
          <PaymentMethodPixQrCode
            qrCodeShow={qrCodeShow}
            charge={charge}
            copyQRCodeError={copyQRCodeError}
            onError={handleCopyQRCodeError}
          />
          <PaymentLinkActiveOpenBankInfo />
          <CopyQRCode
            value={pix.brCode}
            onError={(error) => setCopyQRCodeError(error)}
          />
          <PaymentLinkShareButton charge={charge} pixQrCode={null} />
          <BrowserOnly>
            <ChargePrintPDFButton
              charge={charge}
              trigger={({ disabled, onPrint }) => (
                <ActionButton
                  disabled={disabled}
                  onClick={onPrint}
                  variant='outlined'
                  color='tertiary'
                  size='large'
                  sx={(theme) => ({
                    '&:hover': {
                      color: theme.palette.tertiary.main,
                    },
                    '&:focus': {
                      color: theme.palette.tertiary.main,
                      backgroundColor: theme.palette.common.white,
                    },
                  })}
                  startIcon={
                    <ReceiptIcon color={disabled ? 'disabled' : 'tertiary'} />
                  }
                >
                  {t('DOWNLOAD PIX INVOICE')}
                </ActionButton>
              )}
            />
          </BrowserOnly>
          {renderShowQrCode()}
        </Stack>
      </Stack>
      <Stack sx={{ gap: 1 }}>
        <PaymentLinkActiveInterestsSettings charge={charge} />
        <PaymentMethodPaymentDeadline charge={charge} />
        <PaymentMethodTransactionDetails charge={charge} />
        <PaymentLinkAdditionalInfo charge={charge} />
      </Stack>
    </Stack>
  );
};

export default PaymentMethodPixSelected;
